import {
  accountsForVenue,
  isOrderEligibleAccount,
  resolveDefaultAccountId,
} from '@/lib/utils/account-select'
import { AccountRole, AccountStatus } from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'

/** Why a resolved account was chosen. `peer` is the unique account shared by every
 * scoped row on the same exchange; `surface` is the user's local pick on the current form;
 * `user-default` is the account the user marked as the venue's default. */
export type AccountSource =
  | 'scoped'
  | 'explicit'
  | 'pinned'
  | 'surface'
  | 'peer'
  | 'user-default'
  | 'remembered'
  | 'card'
  | 'venue-default'

export type AccountUnresolvedReason =
  | 'venue-required'
  | 'account-not-found'
  | 'venue-mismatch'
  | 'inactive'
  | 'read-only'
  | 'no-eligible-account'

export type AccountResolution =
  | { status: 'resolved'; accountId: string; source: AccountSource }
  | { status: 'unresolved'; reason: AccountUnresolvedReason }

export interface ResolvableAccount {
  accountId: string
  tradeVenue: TradeVenue
  createdAt: string
  status: AccountStatus
  role: AccountRole
}

export type ResolveAccountInput<T extends ResolvableAccount> = { accounts: T[] } & (
  | {
      /** Read-only display/lookup. Any account status is acceptable. */
      purpose: 'view'
      tradeVenue?: TradeVenue
      explicitAccountId?: string | null
      pinnedAccountId?: string
    }
  | {
      /** Bound to an existing target (position close, order amend, split-account row).
       * Never falls back to another account — an invalid source blocks the action. */
      purpose: 'trade'
      intent: 'scoped'
      tradeVenue: TradeVenue
      scopedAccountId: string
    }
  | {
      /** A new order with no bound account. Priority: surface → peer → user default →
       * remembered → card → venue default. */
      purpose: 'trade'
      intent: 'new'
      tradeVenue?: TradeVenue
      surfaceAccountId?: string
      peerAccountId?: string
      /** The account the user marked as this venue's default — an explicit choice, so it
       * outranks `remembered` (an implicit trace of past account-tab clicks). */
      userDefaultAccountId?: string
      rememberedAccountId?: string
      /** Only a card's explicit/tab-pinned trading account belongs here, never its
       * derived venue default (that is the final `venue-default` tier). */
      cardAccountId?: string
    }
)

const resolved = (accountId: string, source: AccountSource): AccountResolution => ({
  status: 'resolved',
  accountId,
  source,
})

const unresolved = (reason: AccountUnresolvedReason): AccountResolution => ({
  status: 'unresolved',
  reason,
})

/**
 * Single entry point for "which account". Returns the chosen account together with the
 * reason it was chosen (or why none could be), so callers can tell an allowed fallback
 * from a case that must block. Default-account tier is `resolveDefaultAccountId`'s
 * stable oldest-`createdAt` pick — see that function's own doc comment for why.
 */
export function resolveAccount<T extends ResolvableAccount>(
  input: ResolveAccountInput<T>
): AccountResolution {
  const { accounts } = input

  if (input.purpose === 'view') {
    if (input.tradeVenue === undefined) return unresolved('venue-required')
    const venueAccounts = accountsForVenue(accounts, input.tradeVenue)
    const has = (id: string | null | undefined): id is string =>
      !!id && venueAccounts.some((account) => account.accountId === id)
    if (has(input.explicitAccountId)) return resolved(input.explicitAccountId, 'explicit')
    if (has(input.pinnedAccountId)) return resolved(input.pinnedAccountId, 'pinned')
    const fallback = resolveDefaultAccountId(venueAccounts)
    return fallback ? resolved(fallback, 'venue-default') : unresolved('no-eligible-account')
  }

  if (input.intent === 'scoped') {
    const account = accounts.find((candidate) => candidate.accountId === input.scopedAccountId)
    if (!account) return unresolved('account-not-found')
    if (account.tradeVenue !== input.tradeVenue) return unresolved('venue-mismatch')
    if (account.status !== AccountStatus.ACCOUNT_STATUS_ACTIVE) return unresolved('inactive')
    if (account.role === AccountRole.ACCOUNT_ROLE_READ_ONLY) return unresolved('read-only')
    return resolved(account.accountId, 'scoped')
  }

  if (input.tradeVenue === undefined) return unresolved('venue-required')
  const eligible = accountsForVenue(accounts, input.tradeVenue).filter(isOrderEligibleAccount)
  const tiers: [string | undefined, AccountSource][] = [
    [input.surfaceAccountId, 'surface'],
    [input.peerAccountId, 'peer'],
    [input.userDefaultAccountId, 'user-default'],
    [input.rememberedAccountId, 'remembered'],
    [input.cardAccountId, 'card'],
  ]
  for (const [accountId, source] of tiers) {
    if (accountId && eligible.some((account) => account.accountId === accountId)) {
      return resolved(accountId, source)
    }
  }
  const fallback = resolveDefaultAccountId(eligible)
  return fallback ? resolved(fallback, 'venue-default') : unresolved('no-eligible-account')
}
